import * as React from 'react';
import { Slot } from 'radix-ui';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg text-sm font-semibold transition-[transform,background-color,color,box-shadow,opacity,border-color] duration-150 ease-out-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-60',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:opacity-90 motion-safe:active:scale-[0.96]',
        destructive:
          'bg-destructive text-destructive-foreground hover:opacity-90 motion-safe:active:scale-[0.96]',
        outline:
          'border border-border bg-card text-foreground hover:bg-accent hover:text-accent-foreground motion-safe:active:scale-[0.96]',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-accent motion-safe:active:scale-[0.96]',
        ghost:
          'hover:bg-accent hover:text-accent-foreground motion-safe:active:scale-[0.96]',
        link:
          'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 px-6',
        // size-8 visual; ::before extends hit area to 40x40 per a11y minimum.
        // Note: hit area is clipped if any ancestor uses overflow-hidden.
        icon: "relative size-8 rounded-lg p-0 before:absolute before:-inset-1 before:content-['']",
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ComponentPropsWithRef<'button'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function Button({ className, variant, size, asChild = false, ref, ...props }: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button';
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  );
}
Button.displayName = 'Button';

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
